import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const languages = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
];

export const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  const handleLanguageChange = async (languageCode: string) => {
    // Changer immédiatement la langue de l'interface
    i18n.changeLanguage(languageCode);

    // Si l'utilisateur est connecté, mettre à jour la langue en base de données
    if (user) {
      try {
        const { error } = await supabase.rpc('update_user_language', {
          new_language: languageCode,
        });

        if (error) throw error;
      } catch (error) {
        console.error('Error updating language:', error);
        toast.error('Erreur lors de la mise à jour de la langue');
      }
    }
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <Select value={i18n.language} onValueChange={handleLanguageChange}>
      <SelectTrigger className="w-[160px] border-border focus:border-accent focus:ring-2 focus:ring-accent/20">
        <div className="flex items-center gap-2">
          <Globe size={16} className="text-muted-foreground" />
          <SelectValue>
            <span className="flex items-center gap-2">
              <span>{currentLanguage.flag}</span>
              <span className="hidden sm:inline">{currentLanguage.name}</span>
            </span>
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent className="bg-card z-[200]">
        {languages.map((language) => (
          <SelectItem 
            key={language.code} 
            value={language.code}
            className="cursor-pointer hover:bg-muted focus:bg-muted"
          >
            <div className="flex items-center gap-2">
              <span>{language.flag}</span>
              <span>{language.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
